/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * ItemsSection - 물품 항목
 * Displays product items in a table format (product category only)
 */

import { type FC } from "react";
import { Package, CheckCircle2, AlertTriangle, Zap } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { QuoteReviewSectionProps, ItemData } from "../types";

/**
 * Items Section Component
 * Shows product items in a table (product category only)
 */
export const ItemsSection: FC<QuoteReviewSectionProps> = ({
  data,
  onEdit,
  mode,
  onMatchItem,
}) => {
  // Extract items from data
  const isItemData = (item: unknown): item is ItemData =>
    typeof item === "object" && item !== null;
  const items: ItemData[] = Array.isArray(data.items)
    ? data.items.filter(isItemData)
    : [];

  // 🆕 Check if there are any unmatched items (only for admin mode)
  const hasUnmatchedItems =
    mode === "admin" && items.some((item) => !item.itemId);

  // Don't render if no items
  if (items.length === 0) {
    return null;
  }

  return (
    <div className="bg-white rounded-[20px] overflow-hidden">
      {/* Header */}
      <div className="p-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-10 h-10 rounded-[12px] bg-primary/5 flex items-center justify-center text-primary">
            <Package className="w-5 h-5" />
          </div>
          {/* Title & Subtitle */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900">물품 항목</h3>
            <p className="text-sm text-gray-500 font-medium">
              총 {items.length}개 항목
              {hasUnmatchedItems && (
                <span className="ml-2 text-orange-600 font-semibold">
                  • 매칭 필요
                </span>
              )}
              {mode === "admin" && !hasUnmatchedItems && items.length > 0 && (
                <span className="ml-2 text-green-600 font-semibold">
                  • 매칭 완료
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-4 py-1.5 text-gray-400 rounded-[10px] hover:bg-gray-50 hover:text-primary transition-all text-sm font-semibold",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Table Content */}
      <div className="p-0 sm:p-6 overflow-x-auto overflow-y-hidden touch-pan-x">
        <div className="overflow-hidden rounded-[16px] min-w-[600px]">
          <table className="w-full table-fixed text-left min-w-[600px] border-collapse bg-white">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="py-4 px-3 sm:px-4 text-center font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[60px]">
                  No.
                </th>
                <th className="py-4 px-3 sm:px-4 font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[40%]">
                  물품명
                </th>
                <th className="py-4 px-3 sm:px-4 font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[24%]">
                  사양
                </th>
                <th className="py-4 px-3 sm:px-4 text-center font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[80px]">
                  수량
                </th>
                <th className="py-4 px-3 sm:px-4 text-center font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[80px]">
                  단위
                </th>
                {mode === "admin" && (
                  <th className="py-4 px-3 sm:px-4 text-center font-semibold text-gray-400 text-2xs uppercase tracking-wider w-[100px]">
                    관리
                  </th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {items.map((item: ItemData, index: number) => {
                const isMatched = !!item.itemId;
                /*
                  이미 견적이 들어온 줄의 물품을 바꾸면, 그 견적 금액은 새 물품 기준이
                  아니게 된다(자동견적 금액은 매칭된 물품의 공급사 단가에서 나온다).
                  그래도 **막지는 않는다** — 잘못 매칭된 줄을 마스터가 정리할 길이 없어지기
                  때문이다. 대신 여기서 눈에 띄게 표시하고, 확인은 매칭 모달이 받는다.
                */
                const quotedSuppliers = item.quotedSupplierNames ?? [];
                const needsRematchWarning = isMatched && quotedSuppliers.length > 0;

                return (
                  <tr
                    key={index}
                    className={cn(
                      "group transition-colors",
                      isMatched && mode === "admin"
                        ? "bg-green-50/10"
                        : "hover:bg-primary/[0.02]",
                    )}
                  >
                    <td className="py-5 px-3 sm:px-4 text-center font-semibold text-primary bg-gray-50/30 group-hover:bg-primary/5 transition-colors text-base">
                      {index + 1}
                    </td>
                    <td className="py-5 px-3 sm:px-4 text-left align-top">
                      <div className="flex flex-col gap-2 min-w-0">
                        <span className="text-gray-900 font-semibold text-base tracking-tight break-words [overflow-wrap:anywhere]">
                          {item.name ?? item.productName ?? "-"}
                        </span>

                        {/* Matched Standard Item (Admin Mode ONLY) */}
                        {isMatched && mode === "admin" && item.itemName && (
                          <div className="p-2 bg-white border border-green-100 rounded-lg flex items-center gap-3 w-full min-w-0">
                            <div className="w-8 h-8 rounded bg-gray-50 border border-gray-100 overflow-hidden flex-shrink-0">
                              {item.itemImageUrl ? (
                                <img
                                  src={item.itemImageUrl}
                                  alt={item.itemName}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center">
                                  <Package className="w-4 h-4 text-gray-300" />
                                </div>
                              )}
                            </div>
                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-1 min-w-0">
                                <Badge
                                  variant="success"
                                  className="h-4 px-1.5 text-2xs font-semibold"
                                >
                                  표준
                                </Badge>
                                <span className="text-green-700 font-semibold text-xs truncate">
                                  {item.itemName}
                                </span>
                              </div>
                              <div className="text-gray-400 text-2xs flex items-center gap-1.5 font-medium">
                                {item.itemCodeMatch && (
                                  <span>{item.itemCodeMatch}</span>
                                )}
                                {item.itemCodeMatch && item.itemPrice && (
                                  <span>•</span>
                                )}
                                {item.itemPrice && (
                                  <span>
                                    {item.itemPrice.toLocaleString()}원
                                  </span>
                                )}
                              </div>
                            </div>
                            <CheckCircle2 className="w-4 h-4 text-green-500 ml-auto flex-shrink-0" />
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="py-5 px-3 sm:px-4 text-left align-top font-medium text-gray-500 text-base break-words [overflow-wrap:anywhere]">
                      {combineSpecs(item)}
                    </td>
                    <td className="py-5 px-3 sm:px-4 text-center font-semibold bg-gray-50/30 group-hover:bg-primary/5 transition-colors text-base text-gray-900 tracking-tight">
                      {formatQuantity(item.quantity)}
                    </td>
                    <td className="py-5 px-3 sm:px-4 text-center text-gray-500 text-base font-medium">
                      {item.unit ?? "-"}
                    </td>
                    {mode === "admin" && (
                      <td className="py-5 px-3 sm:px-4 text-center align-top">
                        <button
                          type="button"
                          onClick={() => onMatchItem?.(index)}
                          title={
                            needsRematchWarning
                              ? `${quotedSuppliers.join(", ")} 이(가) 이 줄로 견적을 냈습니다. 물품을 바꾸면 그 견적 금액은 새 물품 기준이 아니게 됩니다.`
                              : undefined
                          }
                          className={cn(
                            "px-3 py-1.5 rounded-lg text-xs font-semibold transition-all flex items-center gap-1.5 mx-auto",
                            needsRematchWarning
                              ? "bg-amber-50 border border-amber-300 text-amber-700 hover:bg-amber-100"
                              : isMatched
                                ? "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                                : "bg-primary text-primary-foreground hover:bg-primary-hover",
                          )}
                        >
                          {needsRematchWarning ? (
                            <>
                              <AlertTriangle className="w-3 h-3" />
                              견적 {quotedSuppliers.length}건
                            </>
                          ) : isMatched ? (
                            <>수정</>
                          ) : (
                            <>
                              <Zap className="w-3 h-3" />
                              매칭
                            </>
                          )}
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * 수량을 천 단위 쉼표로 — 작성 중 데이터는 문자열(«100000»)로 들어와 쉼표 없이 보이던 것(GS A2-30).
 */
function formatQuantity(quantity: unknown): string {
  const value = typeof quantity === "string" ? Number(quantity) : quantity;
  return typeof value === "number" && Number.isFinite(value)
    ? value.toLocaleString("ko-KR")
    : "-";
}

/**
 * Combine spec1, spec2, spec3 into single display string
 */
function combineSpecs(item: ItemData): string {
  const specs = [item.spec1, item.spec2, item.spec3]
    .filter(Boolean)
    .map((s) => s!.trim())
    .filter((s) => s.length > 0);

  return specs.length > 0 ? specs.join(", ") : "-";
}

/**
 * Local Badge component
 */
const Badge: FC<{
  children: React.ReactNode;
  variant?: "success" | "outline";
  className?: string;
}> = ({ children, variant, className }) => (
  <span
    className={cn(
      "inline-flex items-center justify-center rounded-full px-2 py-0.5 text-xs font-medium",
      variant === "success"
        ? "bg-green-100 text-green-700"
        : "bg-gray-100 text-gray-700",
      className,
    )}
  >
    {children}
  </span>
);
